import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { onAuthStateChanged, User as FirebaseUser, signOut } from 'firebase/auth';
import { auth } from '../firebase/config';
import { User, getUserProfile } from '../services/userService';
import InactivityWarningModal from '../components/InactivityWarningModal';

interface AuthContextType {
  currentUser: FirebaseUser | null;
  userProfile: User | null;
  loading: boolean;
  rememberMe: boolean;
  setRememberMe: (value: boolean) => void;
}

const AuthContext = createContext<AuthContextType>({
  currentUser: null,
  userProfile: null,
  loading: true,
  rememberMe: false,
  setRememberMe: () => {}
});

export const useAuth = () => useContext(AuthContext);

// Configuration constants
const INACTIVITY_TIMEOUT = 10 * 60 * 1000; // 10 minutes
const WARNING_BEFORE_LOGOUT = 2 * 60 * 1000; // Warn 2 minutes before logout
const REMEMBER_ME_DURATION = 30 * 24 * 60 * 60 * 1000; // 30 days

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [userProfile, setUserProfile] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [showInactivityWarning, setShowInactivityWarning] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  // Timers for inactivity tracking - using refs to avoid stale closures
  const inactivityTimerRef = React.useRef<NodeJS.Timeout | null>(null);
  const warningTimerRef = React.useRef<NodeJS.Timeout | null>(null);

  // Handle logout
  const handleLogout = useCallback(async () => {
    try {
      // Broadcast logout event to all tabs
      localStorage.setItem('logout-event', Date.now().toString());
      localStorage.removeItem('rememberMe');
      localStorage.removeItem('lastActivity');
      
      await signOut(auth);
      setShowInactivityWarning(false);
    } catch (error) {
      console.error('Error during logout:', error);
    }
  }, []);

  // Reset inactivity timers
  const resetInactivityTimers = useCallback(() => {
    // Clear existing timers
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }

    // Don't set timers if no user or if remember me is active
    if (!currentUser) return;

    // Check if remember me is active and still valid
    const rememberMeActive = localStorage.getItem('rememberMe') === 'true';
    const lastActivity = localStorage.getItem('lastActivity');
    
    if (rememberMeActive && lastActivity) {
      const timeSinceLastActivity = Date.now() - parseInt(lastActivity);
      if (timeSinceLastActivity < REMEMBER_ME_DURATION) {
        // Remember me is still valid, update last activity and don't set logout timers
        localStorage.setItem('lastActivity', Date.now().toString());
        return;
      } else {
        // Remember me expired
        localStorage.removeItem('rememberMe');
        localStorage.removeItem('lastActivity');
      }
    }

    // Update last activity timestamp
    localStorage.setItem('lastActivity', Date.now().toString());

    // Set warning timer
    const newWarningTimer = setTimeout(() => {
      setShowInactivityWarning(true);
    }, INACTIVITY_TIMEOUT - WARNING_BEFORE_LOGOUT);

    // Set logout timer
    const newInactivityTimer = setTimeout(() => {
      handleLogout();
    }, INACTIVITY_TIMEOUT);

    warningTimerRef.current = newWarningTimer;
    inactivityTimerRef.current = newInactivityTimer;
  }, [currentUser, handleLogout]);

  // Extend session - reset timers
  const extendSession = useCallback(() => {
    setShowInactivityWarning(false);
    resetInactivityTimers(); // Restart the 10-minute timer
  }, [resetInactivityTimers]);

  // Set up activity listeners
  useEffect(() => {
    if (!currentUser) return;

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];
    
    const handleActivity = () => {
      // Don't reset timers if warning is showing (user needs to explicitly respond)
      if (!showInactivityWarning) {
        resetInactivityTimers();
      }
    };

    events.forEach(event => {
      document.addEventListener(event, handleActivity);
    });

    // Initialize timers on mount
    resetInactivityTimers();

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    };
  }, [currentUser, showInactivityWarning, resetInactivityTimers]);

  // Listen for logout events from other tabs (multi-device logout)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'logout-event') {
        // Another tab logged out, logout this tab too
        signOut(auth).catch(console.error);
        window.location.href = '/login';
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Monitor authentication state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      
      if (user) {
        try {
          const profile = await getUserProfile(user.uid);
          setUserProfile(profile);
          
          // Check if remember me was active
          const rememberMeActive = localStorage.getItem('rememberMe') === 'true';
          setRememberMe(rememberMeActive);
        } catch (error) {
          console.error('Error loading user profile:', error);
        }
      } else {
        setUserProfile(null);
        setRememberMe(false);
        setShowInactivityWarning(false); // Close modal when user logs out
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userProfile,
    loading,
    rememberMe,
    setRememberMe: (value: boolean) => {
      setRememberMe(value);
      if (value) {
        localStorage.setItem('rememberMe', 'true');
        localStorage.setItem('lastActivity', Date.now().toString());
      } else {
        localStorage.removeItem('rememberMe');
        localStorage.removeItem('lastActivity');
      }
    }
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
      <InactivityWarningModal
        isOpen={showInactivityWarning}
        onExtendSession={extendSession}
        onLogout={handleLogout}
        warningDuration={WARNING_BEFORE_LOGOUT}
      />
    </AuthContext.Provider>
  );
};

export default AuthContext; 